// SHA-256 of a file's bytes, hex-encoded — the workbook identity used by the
// provenance ledger (engine agent_docs/model_provenance.md).
//
// Order of preference:
//   1. SubtleCrypto — native and async, available in secure contexts (HTTPS, localhost);
//   2. a Web Worker running the plain-JS implementation below, so hashing a
//      100 MB workbook over plain HTTP does not freeze the page (~60 MB/s);
//   3. the plain-JS implementation on the main thread, if workers are unavailable.

// Self-contained on purpose (no outer references, no destructuring/spread):
// its source is serialized into the worker, and the minifier must not be able
// to rename anything it depends on.
export function sha256HexSync(buffer) {
  var K = [
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
  ];
  var bytes = new Uint8Array(buffer);
  var bitLength = bytes.length * 8;
  var paddedLength = Math.ceil((bytes.length + 9) / 64) * 64;
  var padded = new Uint8Array(paddedLength);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  var view = new DataView(padded.buffer);
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000), false);
  view.setUint32(paddedLength - 4, bitLength >>> 0, false);

  var h = [0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19];
  var w = new Uint32Array(64);
  var offset, i, a, b, c, d, e, f, g, hh, s0, s1, S0, S1, ch, maj, t1, t2;

  for (offset = 0; offset < paddedLength; offset += 64) {
    for (i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4, false);
    for (i = 16; i < 64; i++) {
      s0 = ((w[i - 15] >>> 7) | (w[i - 15] << 25)) ^ ((w[i - 15] >>> 18) | (w[i - 15] << 14)) ^ (w[i - 15] >>> 3);
      s1 = ((w[i - 2] >>> 17) | (w[i - 2] << 15)) ^ ((w[i - 2] >>> 19) | (w[i - 2] << 13)) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    a = h[0]; b = h[1]; c = h[2]; d = h[3]; e = h[4]; f = h[5]; g = h[6]; hh = h[7];
    for (i = 0; i < 64; i++) {
      S1 = ((e >>> 6) | (e << 26)) ^ ((e >>> 11) | (e << 21)) ^ ((e >>> 25) | (e << 7));
      ch = (e & f) ^ (~e & g);
      t1 = (hh + S1 + ch + K[i] + w[i]) >>> 0;
      S0 = ((a >>> 2) | (a << 30)) ^ ((a >>> 13) | (a << 19)) ^ ((a >>> 22) | (a << 10));
      maj = (a & b) ^ (a & c) ^ (b & c);
      t2 = (S0 + maj) >>> 0;
      hh = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0] + a) >>> 0; h[1] = (h[1] + b) >>> 0; h[2] = (h[2] + c) >>> 0; h[3] = (h[3] + d) >>> 0;
    h[4] = (h[4] + e) >>> 0; h[5] = (h[5] + f) >>> 0; h[6] = (h[6] + g) >>> 0; h[7] = (h[7] + hh) >>> 0;
  }
  var hex = "";
  for (i = 0; i < 8; i++) hex += ("00000000" + (h[i] >>> 0).toString(16)).slice(-8);
  return hex;
}

// Runs sha256HexSync inside a Blob-backed Web Worker; the buffer is transferred, not copied
function sha256HexInWorker(buffer) {
  return new Promise((resolve, reject) => {
    const source = "var hash = " + sha256HexSync.toString() + ";" +
      "self.onmessage = function (e) { try { self.postMessage({ hex: hash(e.data) }); } catch (err) { self.postMessage({ error: String(err) }); } };";
    const url = URL.createObjectURL(new Blob([source], { type: "application/javascript" }));
    const worker = new Worker(url);
    const done = (fn, value) => { worker.terminate(); URL.revokeObjectURL(url); fn(value); };
    worker.onmessage = (e) => (e.data && e.data.hex ? done(resolve, e.data.hex) : done(reject, new Error(e.data && e.data.error)));
    worker.onerror = (e) => done(reject, new Error(e.message || "worker failed"));
    worker.postMessage(buffer, [buffer]);
  });
}

export async function sha256Hex(buffer) {
  if (typeof window !== "undefined" && window.crypto && window.crypto.subtle) {
    const digest = await window.crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  if (typeof Worker !== "undefined" && typeof Blob !== "undefined" && typeof URL !== "undefined" && URL.createObjectURL) {
    try {
      return await sha256HexInWorker(buffer);
    } catch (error) {
      console.warn("SHA-256 worker unavailable, hashing on the main thread:", error);
      // the buffer was transferred to the worker only if postMessage succeeded; re-read is the caller's job
      if (buffer.byteLength === 0) throw error;
    }
  }
  return sha256HexSync(buffer);
}
